import { ChangeEvent, useState } from 'react';
import { Calendar, Folder, Image, Trash, User as UserIcon, Users, XCircle } from 'react-feather';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useData, useMutation, useServerErrors } from '../../Shared/api';
import { containsAddress, initFieldEntriesData } from '../../Shared/helpers';
import ImageCropper from '../../Shared/Image/ImageCropper';
import { useLocale } from '../../Shared/locale';
import { useNotifier } from '../../Shared/Notifier/NotificationProvider';
import { Campaign, CampaignField, CampaignFields, CampaignType, Charity, CustomField, CustomFieldChoice, CustomFieldChoiceEntry, CustomFieldEntry, Event, Gender, Locale, Project, User } from '../../Shared/types';
import AddressInputs from '../../Shared/UI/AddressInputs';
import { Button } from '../../Shared/UI/Button';
import { Card } from '../../Shared/UI/Card';
import { Checkbox } from '../../Shared/UI/Checkbox';
import CheckboxLabel from '../../Shared/UI/CheckboxLabel';
import { DateInput } from '../../Shared/UI/DateInput';
import ErrorMessage from '../../Shared/UI/ErrorMessage';
import { FadeIn } from '../../Shared/UI/FadeIn';
import { FileInput } from '../../Shared/UI/FileInput';
import { Icon } from '../../Shared/UI/Icon';
import { Input } from '../../Shared/UI/Input';
import InputDescription from '../../Shared/UI/InputDescription';
import InputGroup from '../../Shared/UI/InputGroup';
import InputLabel from '../../Shared/UI/InputLabel';
import { InputWrapper } from '../../Shared/UI/InputWrapper';
import PhoneNumberInput from '../../Shared/UI/PhoneNumberInput';
import Picker, { PickerItem } from '../../Shared/UI/Picker';
import { Radio } from '../../Shared/UI/Radio';
import { RadioBox } from '../../Shared/UI/RadioBox';
import RadioLabel from '../../Shared/UI/RadioLabel';
import { Select } from '../../Shared/UI/Select';
import ServerErrors from '../../Shared/UI/ServerErrors';
import { Textarea } from '../../Shared/UI/Textarea';
import { useCurrencyInput } from '../../Shared/useCurrencyInput';
import { useDateInput } from '../../Shared/useDateInput';
import { CustomFieldInput, CustomFieldInputFormApi } from '../CustomFields/CustomFieldInput';
import { useRouter } from '../router';

interface EditCampaignFormProps {
  campaign: Campaign & CampaignFields & {
    project: Project & {
      custom_fields: (CustomField & { choices: CustomFieldChoice[]; })[];
    };
    custom_field_entries: (CustomFieldEntry & {
      choice_entries: CustomFieldChoiceEntry[];
    })[];
    event: Event | null;
    team: Campaign | null,
    user: User;
  };
  projects: (PickerItem & {
    allowed_fields: CampaignField[];
    private: boolean;
    custom_fields: (CustomField & { choices: CustomFieldChoice[]; })[];
  })[];
  charity: Charity;
}

export default function EditCampaignForm({
  campaign,
  projects,
  charity,
}: EditCampaignFormProps) {
  const { t } = useTranslation();
  const { handleCurrencyBlur, formatCurrency, parseCurrency } = useCurrencyInput();
  const { handleDateBlur } = useDateInput();
  const { defaultCountry, parseDate, formatDate } = useLocale();
  const { routes } = useRouter();
  const notifier = useNotifier();

  const project = campaign.project;

  const form = useForm({
    defaultValues: {
      project_id: project.id,
      event_id: campaign.event?.id || null,
      title: campaign.title,
      type: campaign.type,
      team_id: campaign.team?.id || null,
      description: campaign.description,
      image: null,
      delete_image: !campaign.image_url,
      start_date: campaign.start_date ? formatDate(campaign.start_date, 'internal_date') : null,
      end_date: campaign.end_date ? formatDate(campaign.end_date, 'internal_date') : null,
      fundraising_target: campaign.fundraising_target ? formatCurrency(campaign.fundraising_target) : null,
      donation_message: campaign.donation_message,
      first_name: campaign.first_name,
      last_name: campaign.last_name,
      email: campaign.user.email,
      gender: campaign.gender,
      date_of_birth: campaign.date_of_birth ? formatDate(campaign.date_of_birth, 'input_date') : '',
      company_name: campaign.company_name,
      phone: campaign.phone,
      street: campaign.street,
      house_number: campaign.house_number,
      extra_address_line: campaign.extra_address_line,
      zip_code: campaign.zip_code,
      city: campaign.city,
      country: campaign.country || defaultCountry,
      email_opt_in: campaign.email_opt_in,
      marketing_email_opt_in: campaign.marketing_email_opt_in,
      phone_opt_in: campaign.phone_opt_in,
      custom_field_entries: initFieldEntriesData(campaign.custom_field_entries, campaign.project.custom_fields),
      locale: campaign.locale,
      private: campaign.private,
    },
  });

  const {
    register, control, setValue, handleSubmit, setError, resetField, watch,
    formState: { errors, defaultValues },
  } = form;

  const [editCampaign, { errors: serverErrors, loading, formKey }] = useMutation(
    routes.edit_campaign(campaign.id),
    {
      onSuccess: () => {
        notifier.notify(t('backend:campaign_saved'));
      },
    },
  );

  useServerErrors(form, serverErrors);

  const [image, setImage] = useState<File>();
  const [croppedImage, setCroppedImage] = useState<File>();
  const deleteImage = watch('delete_image');
  const canDeleteImage = !deleteImage && (!!image || !!campaign.image_url);

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    setImage(event.target.files?.[0] || undefined);
    setCroppedImage(undefined);
    resetField('image'); // Reset errors
    setValue('delete_image', false, { shouldDirty: true });
  };

  const handleCropChange = (file: File, element: HTMLImageElement) => {
    if (element.naturalWidth < 800 || element.naturalHeight < 450) {
      resetImage();
      setError('image', { message: t('shared:validation.image_too_small') });
    } else {
      setCroppedImage(file);
    }
  };

  const resetImage = () => {
    setImage(undefined);
    setCroppedImage(undefined);
    resetField('image'); // Reset errors
    setValue('delete_image', true, { shouldDirty: true });
  };

  const firstName = watch('first_name');
  const lastName = watch('last_name');
  const startDate = watch('start_date');
  const endDate = watch('end_date');
  const gender = watch('gender');
  const companyName = watch('company_name');
  const isTeam = watch('type') === CampaignType.Team;
  const teamId = watch('team_id');
  const projectId = watch('project_id');
  const eventId = watch('event_id');

  const selectedProject = projects.find((project) => project.id === projectId);

  const campaigner = companyName || `${firstName} ${lastName}`.trim();

  const [eventsData] = useData<Event[]>(
    projectId ? routes.project_events_json(projectId) : null,
    {
      onSuccess: (events) => {
        if (events.length === 1) {
          // Preselect the event if there is exactly 1.
          setValue('event_id', events[0].id);
        } else if (projectId === project.id && campaign.event && events.some((event) => event.id === campaign.event?.id)) {
          // Select the original event after changing the project back to the original project.
          setValue('event_id', campaign.event.id);
        } else if (! events.some((event) => event.id === eventId)) {
          // Unset the event if it doesn't exist in the list.
          setValue('event_id', null);
        }
      },
    }
  );

  const events = eventsData || (campaign.event ? [campaign.event] : []);

  const [teamsData] = useData<Campaign[]>(
    projectId ? routes.project_campaigns_json(projectId) : null,
    {
      params: { type: CampaignType.Team, event_id: eventId },
      onSuccess: (teams) => {
        if (projectId === project.id && eventId === (campaign.event?.id || null) && campaign.team && teams.some((team) => team.id === campaign.team?.id)) {
          // Select the original team after changing the project and event back to the original project and event.
          setValue('team_id', campaign.team.id);
        } else {
          // Unset the team.
          setValue('team_id', null);
        }
      },
    }
  );

  const teams = teamsData || (campaign.team ? [campaign.team] : []);

  const submit = async (data: typeof defaultValues) => {
    const dateOfBirth = data?.date_of_birth && parseDate(data.date_of_birth, 'input_date');

    await editCampaign({
      ...data,
      image: (image && croppedImage) || null,
      start_date: data?.start_date,
      end_date: data?.end_date,
      fundraising_target: data?.fundraising_target ? parseCurrency(data.fundraising_target) : null,
      team_id: data?.type === CampaignType.Individual ? data?.team_id : null,
      date_of_birth: dateOfBirth ? formatDate(dateOfBirth, 'internal_date') : null,
      // Unset the prefilled country if no address was entered
      ...!containsAddress(data || {}) && {
        country: null,
      },
    });
  };

  const allowStartDate = !teamId && selectedProject?.allowed_fields.includes(CampaignField.StartDate);
  const allowEndDate = !teamId && selectedProject?.allowed_fields.includes(CampaignField.EndDate);
  const allowCompanyName = !teamId && selectedProject?.allowed_fields.includes(CampaignField.CompanyName);

  return (
    <form onSubmit={handleSubmit(submit)} key={formKey}>
      <Card panels header footer>
        <div>
          <h3>
            {t('backend:edit_campaign_details')}
          </h3>
        </div>

        {serverErrors && (
          <div>
            <ServerErrors errors={serverErrors} scrollIntoView />
          </div>
        )}

        <div className="grid grid-cols-[1fr_3fr]">
          <div>
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:campaign')}
            </h4>
          </div>
          <div className="space-y-6">
            <InputGroup>
              <InputLabel valid={!errors.type} htmlFor="edit_campaign_type_individual">
                {t('backend:campaign_type')}
              </InputLabel>
              <div className="space-y-2">
                <RadioBox disabled={campaign.locked_fields?.includes('type')}>
                  <div className="mr-4">
                    <Radio
                      {...register('type')}
                      value={CampaignType.Individual}
                      id="edit_campaign_type_individual"
                      aria-invalid={!!errors.type}
                      disabled={campaign.locked_fields?.includes('type')}
                    />
                  </div>
                  <div>
                    <strong className="block">
                      <Icon className="mr-1">
                        <UserIcon />
                      </Icon>
                      {t('backend:campaign_type_individual')}
                    </strong>
                    {t('backend:campaign_type_individual_description')}
                  </div>
                </RadioBox>
                <RadioBox disabled={campaign.locked_fields?.includes('type')}>
                  <div className="mr-4">
                    <Radio
                      {...register('type')}
                      value={CampaignType.Team}
                      id="edit_campaign_type_team"
                      aria-invalid={!!errors.type}
                      disabled={campaign.locked_fields?.includes('type')}
                    />
                  </div>
                  <div>
                    <strong className="block">
                      <Icon className="mr-1">
                        <Users />
                      </Icon>
                      {t('backend:campaign_type_team')}
                    </strong>
                    {t('backend:campaign_type_team_description')}
                  </div>
                </RadioBox>
                <ErrorMessage attribute={t('backend:type')} error={errors.type} />
              </div>
            </InputGroup>

            <InputGroup>
              <InputLabel required valid={!errors.project_id} htmlFor="edit_campaign_project">
                {t('backend:project')}
              </InputLabel>
              <Controller
                control={control}
                name="project_id"
                rules={{ required: true }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Picker
                    selected={value}
                    items={projects}
                    onChange={(newProject) => {
                      if (newProject) {
                        // Custom fields depend on the selected project
                        resetField('custom_field_entries', {
                          defaultValue: initFieldEntriesData(campaign.custom_field_entries, newProject.custom_fields),
                        });

                        onChange(newProject.id);
                      }
                    }}
                    onBlur={onBlur}
                    icon={<Folder />}
                    aria-invalid={!!errors.project_id}
                    id="edit_campaign_project"
                    disabled={campaign.locked_fields?.includes('project')}
                  />
                )}
              />
              <ErrorMessage attribute={t('backend:project')} error={errors.project_id} />
            </InputGroup>

            {events && events.length > 0 && (
              <InputGroup>
                <InputLabel required valid={!errors.event_id} htmlFor="edit_campaign_event">
                  {t('backend:event')}
                </InputLabel>
                <Controller
                  control={control}
                  name="event_id"
                  rules={{ required: true }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Picker
                      selected={value}
                      items={events}
                      onChange={(value) => onChange(value?.id || null)}
                      onBlur={onBlur}
                      icon={<Calendar />}
                      aria-invalid={!!errors.event_id}
                      id="edit_campaign_event"
                      disabled={campaign.locked_fields?.includes('project')}
                    />
                  )}
                />
                <ErrorMessage attribute={t('backend:event')} error={errors.event_id} />
              </InputGroup>
            )}

            {!isTeam && (
              <InputGroup>
                <InputLabel valid={!errors.team_id} htmlFor="edit_campaign_team">
                  {t('backend:team')}
                </InputLabel>
                <Controller
                  control={control}
                  name="team_id"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Picker
                      selected={value}
                      items={teams || []}
                      onChange={(value) => onChange(value?.id || null)}
                      onBlur={onBlur}
                      icon={<Users />}
                      aria-invalid={!!errors.team_id}
                      placeholder={t('backend:no_team')}
                      id="edit_campaign_team"
                      optional
                      disabled={campaign.locked_fields?.includes('team')}
                    />
                  )}
                />
                <ErrorMessage attribute={t('backend:team')} error={errors.team_id} />
              </InputGroup>
            )}

            <InputGroup>
              <InputLabel htmlFor="edit_campaign_title">
                {t('backend:campaign_title')}
              </InputLabel>
              {!teamId && (
                <Input
                  {...register('title')}
                  id="edit_campaign_title"
                  placeholder={`${firstName} ${lastName}`}
                  aria-invalid={!!errors.title}
                  disabled={campaign.locked_fields?.includes('title')}
                />
              )}
              {teamId && (
                <Input
                  id="edit_campaign_title"
                  value={campaigner}
                  disabled
                />
              )}
              <ErrorMessage error={errors.title} attribute={t('backend:campaign_title')} />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="edit_campaign_image">
                {t('backend:image')}
              </InputLabel>
              <InputDescription>
                {t('backend:campaign_image_description')}
              </InputDescription>
              <div className="max-w-[610px] p-4 border rounded-md border-slate-250">
                <div
                  className="flex items-center justify-center mb-4 transition-all rounded-sm bg-slate-50 aspect-video"
                >
                  {!image && !deleteImage && campaign.image_url && (
                    <img
                      src={campaign.image_url}
                      alt={campaign.title}
                      className="object-cover w-full h-full rounded-sm"
                    />
                  )}
                  {image && (
                    <ImageCropper
                      image={image}
                      onChange={handleCropChange}
                      aspect={16 / 9}
                    />
                  )}
                  {((!image && !campaign.image_url) || deleteImage) && (
                    <Icon className="!w-12 !h-12 text-slate-250">
                      <Image strokeWidth={1} />
                    </Icon>
                  )}
                </div>
                <div className="flex space-x-2">
                  <FileInput
                    {...register('image')}
                    accept=".jpg,.jpeg,.png"
                    id="edit_campaign_image"
                    aria-invalid={!!errors.image}
                    onChange={handleImageChange}
                  >
                    <Icon className="mr-1">
                      <Image />
                    </Icon>
                    {t('shared:form.select')}
                  </FileInput>
                  {canDeleteImage && (
                    <Button onClick={() => resetImage()} variant="tertiary" size="sm">
                      <Icon className="mr-1">
                        <Trash />
                      </Icon>
                      {t('shared:form.delete')}
                    </Button>
                  )}
                </div>
              </div>
              <ErrorMessage error={errors.image} attribute={t('backend:image')} />
            </InputGroup>

            <FadeIn
              when={allowStartDate || allowEndDate}
              render={() => (
                <div className="grid grid-cols-[1fr_1fr] gap-6">
                  {allowStartDate && (
                    <InputGroup>
                      <InputLabel htmlFor="edit_campaign_start_date">
                        {t('backend:start_date')}
                      </InputLabel>
                      <Controller
                        control={control}
                        name="start_date"
                        rules={{ max: endDate || undefined }}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <DateInput
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            ariaInvalid={!!errors.start_date}
                            optional
                            endDate={endDate}
                            selectsStart
                            id="edit_campaign_start_date"
                          />
                        )}
                      />
                      <ErrorMessage
                        error={errors.start_date}
                        attribute={t('backend:start_date')}
                        values={{ max: t('backend:end_date') }}
                      />
                    </InputGroup>
                  )}
                  {allowEndDate && (
                    <InputGroup>
                      <InputLabel htmlFor="edit_campaign_end_date">
                        {t('backend:end_date')}
                      </InputLabel>
                      <Controller
                        control={control}
                        name="end_date"
                        rules={{ min: startDate || undefined }}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <DateInput
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            aria-invalid={!!errors.end_date}
                            optional
                            startDate={startDate}
                            selectsEnd
                            id="edit_campaign_end_date"
                          />
                        )}
                      />
                      <ErrorMessage
                        error={errors.end_date}
                        attribute={t('backend:end_date')}
                        values={{ min: t('backend:start_date') }}
                      />
                    </InputGroup>
                  )}
                </div>
              )}
            />

            {selectedProject?.allowed_fields.includes(CampaignField.FundraisingTarget) && (
              <InputGroup>
                <InputLabel htmlFor="edit_campaign_fundraising_target">
                  {t('backend:fundraising_target')}
                </InputLabel>
                <InputWrapper>
                  <span>
                  €
                  </span>
                  <Input
                    {...register('fundraising_target')}
                    id="edit_campaign_fundraising_target"
                    className="pl-7"
                    onBlur={(event) => {
                      handleCurrencyBlur(event);
                      setValue('fundraising_target', event.target.value);
                    }}
                    aria-invalid={!!errors.fundraising_target}
                    min="0"
                  />
                </InputWrapper>
                <ErrorMessage error={errors.fundraising_target} attribute={t('backend:fundraising_target')} />
              </InputGroup>
            )}

            <InputGroup>
              <InputLabel htmlFor="edit_campaign_description">
                {t('backend:campaign_description')}
              </InputLabel>
              <InputDescription>
                {t('backend:campaign_description_description')}
              </InputDescription>
              <Textarea
                {...register('description')}
                id="edit_campaign_description"
                aria-invalid={!!errors.description}
                rows={6}
              />
              <ErrorMessage error={errors.description} attribute={t('backend:campaign_description')} />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="edit_campaign_donation_message">
                {t('backend:donation_message')}
              </InputLabel>
              <InputDescription>
                {t('backend:donation_message_description')}
              </InputDescription>
              <Textarea
                {...register('donation_message')}
                id="edit_campaign_donation_message"
                aria-invalid={!!errors.donation_message}
                rows={3}
              />
              <ErrorMessage error={errors.donation_message} attribute={t('backend:donation_message')} />
            </InputGroup>

            {!selectedProject?.private && (
              <InputGroup>
                <InputLabel htmlFor="edit_campaign_private">
                  {t('backend:visibility')}
                </InputLabel>
                <CheckboxLabel>
                  <Checkbox {...register('private')}/>
                  <span>
                    {t('backend:campaign_private_label')}
                  </span>
                </CheckboxLabel>
              </InputGroup>
            )}
          </div>
        </div>

        <div className="grid grid-cols-[1fr_3fr]">
          <div>
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:personal')}
            </h4>
          </div>
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <InputGroup>
                <InputLabel required valid={!errors.first_name} htmlFor="edit_campaign_first_name">
                  {t('backend:first_name')}
                </InputLabel>
                <Input
                  {...register('first_name', { required: true })}
                  id="edit_campaign_first_name"
                  className="mr-4"
                  aria-invalid={!!errors.first_name}
                  disabled={campaign.locked_fields?.includes('user')}
                />
                <ErrorMessage attribute={t('backend:first_name')} error={errors.first_name }/>
              </InputGroup>

              <InputGroup>
                <InputLabel required valid={!errors.last_name} htmlFor="edit_campaign_last_name">
                  {t('backend:last_name')}
                </InputLabel>
                <Input
                  {...register('last_name', { required: true })}
                  id="edit_campaign_last_name"
                  className="mr-4"
                  aria-invalid={!!errors.last_name}
                  disabled={campaign.locked_fields?.includes('user')}
                />
                <ErrorMessage attribute={t('backend:last_name')} error={errors.last_name }/>
              </InputGroup>
            </div>

            <InputGroup>
              <InputLabel required valid={!errors.email} htmlFor="edit_campaign_email">
                {t('backend:email_address')}
              </InputLabel>
              <Input
                {...register('email', { required: true })}
                inputMode="email"
                id="edit_campaign_email"
                className="mr-4"
                aria-invalid={!!errors.email}
                disabled={campaign.locked_fields?.includes('user')}
              />
              <ErrorMessage attribute={t('backend:email_address')} error={errors.email }/>
            </InputGroup>

            {selectedProject?.allowed_fields.includes(CampaignField.Gender) && (
              <InputGroup>
                <InputLabel valid={!errors.gender} htmlFor="edit_campaign_gender">
                  {t('backend:gender')}
                </InputLabel>
                <div className="flex space-x-3">
                  <RadioLabel>
                    <Radio
                      {...register('gender')}
                      value={Gender.Male}
                      aria-invalid={!!errors.gender}
                    />
                    <span>
                      {t('shared:genders.male')}
                    </span>
                  </RadioLabel>
                  <RadioLabel>
                    <Radio
                      {...register('gender')}
                      value={Gender.Female}
                      aria-invalid={!!errors.gender}
                    />
                    <span>
                      {t('shared:genders.female')}
                    </span>
                  </RadioLabel>
                  <RadioLabel>
                    <Radio
                      {...register('gender')}
                      value={Gender.Nonbinary}
                      aria-invalid={!!errors.gender}
                    />
                    <span>
                      {t('shared:genders.nonbinary')}
                    </span>
                  </RadioLabel>
                  {gender && (
                    <Button
                      onClick={(event) => {
                        event.preventDefault(); setValue('gender', null);
                      }}
                      variant="tertiary"
                      size="xs"
                      aria-label={t('shared:filter.clear')}
                    >
                      <Icon>
                        <XCircle />
                      </Icon>
                    </Button>
                  )}
                </div>
                <ErrorMessage error={errors.gender} attribute={t('frontend:gender')} />
              </InputGroup>
            )}

            {selectedProject?.allowed_fields.includes(CampaignField.DateOfBirth) && (
              <InputGroup>
                <InputLabel htmlFor="edit_campaign_date_of_birth">
                  {t('backend:date_of_birth')}
                </InputLabel>
                <Input
                  {...register('date_of_birth')}
                  onBlur={(event) => {
                    handleDateBlur(event);
                    setValue('date_of_birth', event.target.value);
                  }}
                  id="edit_campaign_date_of_birth"
                  placeholder={t('shared:date_input.date_format_placeholder')}
                  aria-invalid={!!errors.date_of_birth}
                />
                <ErrorMessage error={errors.date_of_birth} attribute={t('backend:date_of_birth')} />
              </InputGroup>
            )}

            <FadeIn
              when={allowCompanyName}
              render={() => (
                <InputGroup>
                  <InputLabel valid={!errors.company_name} htmlFor="edit_campaign_company_name">
                    {t('backend:company_name')}
                  </InputLabel>
                  <Input
                    {...register('company_name')}
                    id="edit_campaign_company_name"
                    aria-invalid={!!errors.company_name}
                  />
                  <ErrorMessage error={errors.company_name} attribute={t('backend:company_name')} />
                </InputGroup>
              )}
            />

            {selectedProject?.allowed_fields.includes(CampaignField.Phone) && (
              <InputGroup>
                <InputLabel htmlFor="edit_campaign_phone">
                  {t('backend:phone_number')}
                </InputLabel>
                <Controller
                  control={control}
                  name="phone"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <PhoneNumberInput
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      country={campaign.country}
                      id="edit_campaign_phone"
                      ariaInvalid={!!errors.phone}
                    />
                  )}
                />
                <ErrorMessage error={errors.phone} attribute={t('backend:phone_number')} />
              </InputGroup>
            )}

            {selectedProject?.allowed_fields.includes(CampaignField.Address) && (
              <AddressInputs form={form} required={false} />
            )}
          </div>
        </div>

        {selectedProject && selectedProject.custom_fields.length > 0 && (
          <div className="grid grid-cols-[1fr_3fr]">
            <div>
              <h4 className="font-medium text-slate-500 mb-1">
                {t('backend:extra')}
              </h4>
            </div>
            <div className="space-y-6">
              {selectedProject.custom_fields.map((field, index) => (
                <CustomFieldInput
                  form={form as unknown as CustomFieldInputFormApi}
                  index={index}
                  field={{ ...field, required: false }}
                  key={field.id}
                />
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-[1fr_3fr]">
          <div>
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:language')}
            </h4>
          </div>
          <div className="space-y-6">
            <InputGroup>
              <InputLabel required valid={!errors.locale} htmlFor="edit_campaign_locale">
                {t('backend:language')}
              </InputLabel>
              <Select
                {...register('locale', { required: true })}
                id="edit_campaign_locale"
                aria-invalid={!!errors.locale}
              >
                <option value={Locale.EN}>
                  {t(`shared:locales.${Locale.EN}`)}
                </option>
                <option value={Locale.NL}>
                  {t(`shared:locales.${Locale.NL}`)}
                </option>
              </Select>
              <ErrorMessage error={errors.locale} attribute={t('backend:language')} />
            </InputGroup>
          </div>
        </div>

        <div className="grid grid-cols-[1fr_3fr]">
          <div>
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:opt_ins')}
            </h4>
          </div>
          <div className="space-y-6">
            <InputGroup>
              <InputLabel valid={!errors.email_opt_in} htmlFor="campaign_email_opt_in" >
                {t('backend:email_notifications')}
              </InputLabel>
              <CheckboxLabel className="flex space-x-2 font-normal">
                <Checkbox
                  {...register('email_opt_in')}
                  id="campaign_email_opt_in"
                  className="mt-1"
                />
                <span>
                  {t('backend:campaign_email_opt_in_text')}
                </span>
              </CheckboxLabel>
            </InputGroup>

            <InputGroup>
              <InputLabel>
                {t('backend:marketing_opt_ins')}
              </InputLabel>
              <CheckboxLabel>
                <Checkbox
                  {...register('marketing_email_opt_in')}
                  id="campaign_marketing_email_opt_in"
                />
                <span>
                  {t('backend:email_opt_in_text', { charity: charity.title })}
                </span>
              </CheckboxLabel>
              <CheckboxLabel>
                <Checkbox
                  {...register('phone_opt_in')}
                  id="campaign_phone_opt_in"
                />
                <span>
                  {t('backend:phone_opt_in_text', { charity: charity.title })}
                </span>
              </CheckboxLabel>
            </InputGroup>
          </div>
        </div>

        <div className="text-right">
          <Button
            type="submit"
            loading={loading}
            className="ml-auto"
          >
            {t('backend:save')}
          </Button>
        </div>
      </Card>
    </form>
  );
}
